import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Link, useRouter } from '../../util/router';
import { useAuth } from '../../util/auth';
import { getProfilePhoto } from '../../util/util';
import showrunnerLogo from '../../assets/images/SHOWRUNNER.png';
import { HomeIcon, MapsIcon, SearchIcon, ProfileIcon, MenuIcon } from './icons';
import AppThreeBarMenu from './AppThreeBarMenu';


const AppThreeBarMenuContext = createContext({ open: () => {} });
export const useAppThreeBarMenu = () => useContext(AppThreeBarMenuContext);

const LogoIcon = ({ className }) => (
  <span
    aria-hidden
    className={`inline-block bg-current ${className}`}
    style={{
      WebkitMaskImage: `url(${showrunnerLogo})`,
      maskImage: `url(${showrunnerLogo})`,
      WebkitMaskSize: 'contain',
      maskSize: 'contain',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
    }}
  />
);

function isProfileSection(pathname, username) {
  return (
    pathname.startsWith('/seen-shows') ||
    pathname.startsWith('/want-to-see') ||
    pathname.startsWith('/following') ||
    pathname === '/my-lists' ||
    pathname.startsWith('/settings') ||
    (!!username && pathname === `/profile/${username}`)
  );
}

export function useTabs() {
  const auth = useAuth();
  const { pathname } = useRouter();
  const username = auth.user?.username;
  const profilePath = !auth.user
    ? '/auth/signin'
    : username
      ? `/profile/${username}`
      : '/settings/general';

  return [
    { key: 'feed', label: 'Explore', to: '/feed', Icon: HomeIcon, active: pathname === '/feed' },
    { key: 'map', label: 'Map', to: '/map', Icon: MapsIcon, active: pathname === '/map' },
    {
      key: 'featured',
      label: 'Featured',
      to: '/featured',
      isLogo: true,
      active: pathname === '/featured',
    },
    {
      key: 'search',
      label: 'Search',
      to: '/search',
      Icon: SearchIcon,
      active: pathname === '/search' || pathname.startsWith('/org/'),
    },
    {
      key: 'profile',
      label: 'Profile',
      to: profilePath,
      Icon: ProfileIcon,
      active: isProfileSection(pathname, username),
    },
  ];
}

function useUserPhoto() {
  const auth = useAuth();
  const [photo, setPhoto] = useState(null);
  const userId = auth.user?.id;

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setPhoto(null);
      return undefined;
    }
    getProfilePhoto(userId)
      .then(url => !cancelled && setPhoto(url ?? null))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return photo;
}

function TabGlyph({ tab, photo, className }) {
  if (tab.isLogo) return <LogoIcon className={className} />;
  if (tab.key === 'profile' && photo) {
    return (
      <img
        src={photo}
        alt=""
        className={`${className} rounded-full object-cover ${
          tab.active ? 'ring-2 ring-secondary-red ring-offset-2' : ''
        }`}
      />
    );
  }
  const { Icon } = tab;
  return <Icon active={tab.active} className={className} />;
}

function SideNav({ onToggleMenu, menuOpen, photo }) {
  const tabs = useTabs();

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-y-0 left-0 z-[90] hidden w-20 flex-col items-center border-r border-slate-200 bg-white py-6 md:flex"
    >
      <ul className="flex flex-1 flex-col items-center gap-2">
        {tabs.map(tab => (
          <li key={tab.key}>
            <Link
              to={tab.to}
              aria-label={tab.label}
              aria-current={tab.active ? 'page' : undefined}
              className={`group relative flex h-12 w-12 items-center justify-center rounded-xl transition-colors hover:bg-slate-100 ${
                tab.active ? 'text-secondary-red' : 'text-slate-500 hover:text-black'
              }`}
            >
              <TabGlyph tab={tab} photo={photo} className={tab.isLogo ? 'h-11 w-11' : 'h-7 w-7'} />
              <span className="pointer-events-none absolute left-full ml-3 whitespace-nowrap rounded-md bg-black px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                {tab.label}
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onToggleMenu}
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
        className={`flex h-12 w-12 items-center justify-center rounded-xl hover:bg-slate-100 ${
          menuOpen ? 'bg-slate-100 text-black' : 'text-slate-500 hover:text-black'
        }`}
      >
        <MenuIcon className="h-7 w-7" />
      </button>
    </nav>
  );
}

function BottomTabBar({ photo }) {
  const tabs = useTabs();

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-[90] rounded-t-3xl bg-white px-6 pt-3 md:hidden"
      style={{
        paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
        boxShadow: '0px -10px 12px 0px rgba(0, 0, 0, 0.04)',
      }}
    >
      <ul className="flex items-center justify-between">
        {tabs.map(tab => (
          <li key={tab.key} className="flex flex-1 justify-center">
            <Link
              to={tab.to}
              aria-label={tab.label}
              aria-current={tab.active ? 'page' : undefined}
              className={`flex h-11 w-11 items-center justify-center ${
                tab.active ? 'text-secondary-red' : 'text-slate-500'
              }`}
              onClick={() => {
                if (tab.key === 'feed' && tab.active) window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <TabGlyph tab={tab} photo={photo} className="h-7 w-7" />
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function MobileMenuButton({ className = '' }) {
  const { open } = useAppThreeBarMenu();
  return (
    <button
      type="button"
      onClick={open}
      aria-label="Open menu"
      className={`flex h-10 w-10 items-center justify-center rounded-lg text-black hover:bg-black/5 md:hidden ${className}`}
    >
      <MenuIcon className="h-6 w-6" />
    </button>
  );
}

function AppShell({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const photo = useUserPhoto();
  const { pathname } = useRouter();
  const openMenu = useCallback(() => setMenuOpen(true), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <AppThreeBarMenuContext.Provider value={{ open: openMenu }}>
      <div className="min-h-screen bg-white md:pl-20">
        <SideNav
          onToggleMenu={() => setMenuOpen(open => !open)}
          menuOpen={menuOpen}
          photo={photo}
        />
        <main className="min-h-screen pb-24 md:pb-0">{children}</main>
        <BottomTabBar photo={photo} />
        <AppThreeBarMenu isOpen={menuOpen} onClose={closeMenu} />
      </div>
    </AppThreeBarMenuContext.Provider>
  );
}

export default AppShell;
